import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Rating } from "primereact/rating";
import { Toast } from "primereact/toast";
import { FC, useContext, useRef, useState } from "react";
import { getErrorTitle } from "../../../constants/constants";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  appWideDialogStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";

interface UserFeedbackModalProps {
  userId: string | undefined;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
}

const UserFeedbackModal: FC<UserFeedbackModalProps> = ({
  userId,
  isOpen,
  toggleModal,
}) => {
  const toast = useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState<string>("");

  const onsubmit = async (e) => {
    e?.preventDefault();
    if (comment.trim() === "" || rating === 0) {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Please provide both rating and comment.', life: 3000 });
      return;
    }

    setLoading(true);
    try {
      await backendAPIStore.init();
      await backendAPIStore.addNewReview({
        userId: userId,
        reviewStar: rating,
        reviewComment: comment,
      });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Feedback submitted successfully.', life: 3000 });
      setRating(0);
      setComment("");
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: 'Failed to submit feedback.', life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const headerElement = renderSurfaceHeader(
    "User Feedback",
    "Leave a quick review and rating for this user interaction.",
    "pi pi-star",
  );

  return (
    <Dialog visible={isOpen} modal header={headerElement} style={appWideDialogStyle}
      headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
      onHide={() => { if (!isOpen) return; toggleModal(false); }}>
      <Toast ref={toast} />
      <div style={appAccentPanelStyle} className="mb-3">
        <div style={{ fontWeight: 700, color: "#9a3412" }}>Review summary</div>
        <div className="mt-2" style={appMutedTextStyle}>
          Add a star rating and a short comment to capture your experience clearly.
        </div>
      </div>
      <form style={appFormSectionStyle}>
          <div className="field">
            <Rating value={rating} onChange={(e) => setRating(e.value!)} className="m-2 justify-content-center" cancel={false} />
            <InputTextarea id="reviewComment" rows={5} cols={30} className="p-inputtext p-component p-inputtextarea w-full"
              value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
          <Button type="submit" severity="warning" label="Save" className="mt-2" rounded outlined onClick={(e) => onsubmit(e)} />
      </form>
    </Dialog>
  );
};

export default UserFeedbackModal;
